import types from '../mutation-types';
import KanbanBoardsAPI from '../../api/kanban/boards';
import KanbanColumnsAPI from '../../api/kanban/columns';
import KanbanCardsAPI from '../../api/kanban/cards';
import KanbanRoutingRulesAPI from '../../api/kanban/routing_rules';

export const state = {
  boards: [],
  columns: [],
  cards: [],
  routingRules: [],
  uiFlags: {
    isFetchingBoards: false,
    isFetchingColumns: false,
    isFetchingCards: false,
  },
};

export const getters = {
  getBoards(_state) {
    return _state.boards;
  },
  getBoardById: _state => id => {
    return _state.boards.find(board => board.id === Number(id));
  },
  getColumnsByBoardId: _state => boardId => {
    return _state.columns.filter(
      column => column.kanban_board_id === Number(boardId)
    );
  },
  getCardsByBoardId: _state => boardId => {
    const columnIds = _state.columns
      .filter(column => column.kanban_board_id === Number(boardId))
      .map(column => column.id);
    return _state.cards.filter(card =>
      columnIds.includes(card.kanban_column_id)
    );
  },
  getRoutingRulesByColumnId: _state => columnId => {
    return _state.routingRules.filter(
      rule => rule.kanban_column_id === Number(columnId)
    );
  },
  getUIFlags(_state) {
    return _state.uiFlags;
  },
};

export const actions = {
  getBoards: async function getBoards({ commit }) {
    commit(types.SET_KANBAN_UI_FLAG, { isFetchingBoards: true });
    try {
      const response = await KanbanBoardsAPI.get();
      commit(types.SET_KANBAN_BOARDS, response.data.payload);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_KANBAN_UI_FLAG, { isFetchingBoards: false });
    }
  },

  createBoard: async function createBoard({ commit }, payload) {
    const response = await KanbanBoardsAPI.create(payload);
    commit(types.ADD_KANBAN_BOARD, response.data);
    return response.data;
  },

  updateBoard: async function updateBoard({ commit }, { id, ...payload }) {
    const response = await KanbanBoardsAPI.update(id, payload);
    commit(types.EDIT_KANBAN_BOARD, response.data);
    return response.data;
  },

  deleteBoard: async function deleteBoard({ commit }, boardId) {
    await KanbanBoardsAPI.delete(boardId);
    commit(types.DELETE_KANBAN_BOARD, boardId);
  },

  getColumns: async function getColumns({ commit }, boardId) {
    commit(types.SET_KANBAN_UI_FLAG, { isFetchingColumns: true });
    try {
      const response = await KanbanColumnsAPI.index(boardId);
      commit(types.SET_KANBAN_COLUMNS, response.data.payload);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_KANBAN_UI_FLAG, { isFetchingColumns: false });
    }
  },

  createColumn: async function createColumn(
    { commit },
    { boardId, ...payload }
  ) {
    const response = await KanbanColumnsAPI.create(boardId, payload);
    commit(types.ADD_KANBAN_COLUMN, response.data);
    return response.data;
  },

  updateColumn: async function updateColumn(
    { commit },
    { boardId, columnId, ...payload }
  ) {
    const response = await KanbanColumnsAPI.update(boardId, columnId, payload);
    commit(types.EDIT_KANBAN_COLUMN, response.data);
    return response.data;
  },

  deleteColumn: async function deleteColumn({ commit }, { boardId, columnId }) {
    await KanbanColumnsAPI.delete(boardId, columnId);
    commit(types.DELETE_KANBAN_COLUMN, columnId);
  },

  getCards: async function getCards({ commit }, boardId) {
    commit(types.SET_KANBAN_UI_FLAG, { isFetchingCards: true });
    try {
      const response = await KanbanCardsAPI.index(boardId);
      commit(types.SET_KANBAN_CARDS, response.data.payload);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_KANBAN_UI_FLAG, { isFetchingCards: false });
    }
  },

  getMoreCards: async function getMoreCards(
    { commit },
    { boardId, columnId, page }
  ) {
    commit(types.SET_KANBAN_UI_FLAG, { isFetchingCards: true });
    try {
      const response = await KanbanCardsAPI.index(boardId, {
        column_id: columnId,
        page,
      });
      commit(types.APPEND_KANBAN_CARDS, response.data.payload);
      return response.data;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_KANBAN_UI_FLAG, { isFetchingCards: false });
    }
  },

  createCard: async function createCard({ commit }, { boardId, ...payload }) {
    const response = await KanbanCardsAPI.create(boardId, payload);
    commit(types.ADD_KANBAN_CARD, response.data);
    return response.data;
  },

  updateCard: async function updateCard(
    { commit },
    { boardId, cardId, ...payload }
  ) {
    const response = await KanbanCardsAPI.update(boardId, cardId, payload);
    commit(types.EDIT_KANBAN_CARD, response.data);
    return response.data;
  },

  deleteCard: async function deleteCard({ commit }, { boardId, cardId }) {
    await KanbanCardsAPI.delete(boardId, cardId);
    commit(types.DELETE_KANBAN_CARD, cardId);
  },

  reorderColumns: async function reorderColumns(
    { commit, dispatch },
    { boardId, orderedIds }
  ) {
    commit(types.REORDER_KANBAN_COLUMNS, { orderedIds });
    try {
      await KanbanColumnsAPI.reorder(boardId, orderedIds);
    } catch (error) {
      await dispatch('getColumns', boardId);
      throw new Error(error);
    }
  },

  reorderCards: async function reorderCards(
    { commit, dispatch },
    { boardId, kanbanColumnId, orderedIds }
  ) {
    commit(types.REORDER_KANBAN_CARDS, { kanbanColumnId, orderedIds });
    try {
      await KanbanCardsAPI.reorder(boardId, kanbanColumnId, orderedIds);
    } catch (error) {
      await dispatch('getCards', boardId);
      throw new Error(error);
    }
  },

  getRoutingRules: async function getRoutingRules(
    { commit },
    { boardId, columnId }
  ) {
    const response = await KanbanRoutingRulesAPI.index(boardId, columnId);
    commit(types.SET_KANBAN_ROUTING_RULES, response.data.payload);
    return response.data.payload;
  },

  createRoutingRule: async function createRoutingRule(
    { commit },
    { boardId, columnId, labelId }
  ) {
    const response = await KanbanRoutingRulesAPI.create(
      boardId,
      columnId,
      labelId
    );
    commit(types.ADD_KANBAN_ROUTING_RULE, response.data);
    return response.data;
  },

  deleteRoutingRule: async function deleteRoutingRule(
    { commit },
    { boardId, columnId, ruleId }
  ) {
    await KanbanRoutingRulesAPI.delete(boardId, columnId, ruleId);
    commit(types.DELETE_KANBAN_ROUTING_RULE, ruleId);
  },
};

export const mutations = {
  [types.SET_KANBAN_UI_FLAG](_state, flag) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...flag,
    };
  },

  [types.SET_KANBAN_BOARDS](_state, data) {
    _state.boards = data;
  },

  [types.ADD_KANBAN_BOARD](_state, data) {
    _state.boards.push(data);
  },

  [types.EDIT_KANBAN_BOARD](_state, data) {
    const boardIndex = _state.boards.findIndex(board => board.id === data.id);
    if (boardIndex > -1) {
      _state.boards[boardIndex] = data;
    }
  },

  [types.DELETE_KANBAN_BOARD](_state, boardId) {
    const columnIds = _state.columns
      .filter(column => column.kanban_board_id === boardId)
      .map(column => column.id);
    _state.boards = _state.boards.filter(board => board.id !== boardId);
    _state.columns = _state.columns.filter(
      column => column.kanban_board_id !== boardId
    );
    _state.cards = _state.cards.filter(
      card => !columnIds.includes(card.kanban_column_id)
    );
  },

  [types.SET_KANBAN_COLUMNS](_state, data) {
    _state.columns = data;
  },

  [types.ADD_KANBAN_COLUMN](_state, data) {
    _state.columns.push(data);
  },

  [types.EDIT_KANBAN_COLUMN](_state, data) {
    const columnIndex = _state.columns.findIndex(
      column => column.id === data.id
    );
    if (columnIndex > -1) {
      _state.columns[columnIndex] = data;
    }
  },

  [types.DELETE_KANBAN_COLUMN](_state, columnId) {
    _state.columns = _state.columns.filter(column => column.id !== columnId);
    _state.cards = _state.cards.filter(
      card => card.kanban_column_id !== columnId
    );
  },

  [types.SET_KANBAN_CARDS](_state, data) {
    _state.cards = data;
  },

  [types.APPEND_KANBAN_CARDS](_state, data) {
    const existingIds = new Set(_state.cards.map(card => card.id));
    data.forEach(card => {
      if (!existingIds.has(card.id)) {
        _state.cards.push(card);
      }
    });
  },

  [types.ADD_KANBAN_CARD](_state, data) {
    _state.cards.push(data);
  },

  [types.EDIT_KANBAN_CARD](_state, data) {
    const cardIndex = _state.cards.findIndex(card => card.id === data.id);
    if (cardIndex > -1) {
      _state.cards[cardIndex] = data;
    }
  },

  [types.DELETE_KANBAN_CARD](_state, cardId) {
    _state.cards = _state.cards.filter(card => card.id !== cardId);
  },

  [types.REORDER_KANBAN_COLUMNS](_state, { orderedIds }) {
    const positionsById = new Map(
      orderedIds.map((id, index) => [Number(id), index])
    );
    _state.columns.forEach(column => {
      if (positionsById.has(column.id)) {
        column.position = positionsById.get(column.id);
      }
    });
    _state.columns = [..._state.columns].sort(
      (a, b) => a.position - b.position
    );
  },

  [types.REORDER_KANBAN_CARDS](_state, { kanbanColumnId, orderedIds }) {
    const positionsById = new Map(
      orderedIds.map((id, index) => [Number(id), index])
    );
    _state.cards.forEach(card => {
      if (positionsById.has(card.id)) {
        card.kanban_column_id = kanbanColumnId;
        card.position = positionsById.get(card.id);
      }
    });
    _state.cards = [..._state.cards].sort(
      (a, b) =>
        a.kanban_column_id - b.kanban_column_id || a.position - b.position
    );
  },

  [types.SET_KANBAN_ROUTING_RULES](_state, data) {
    _state.routingRules = data;
  },

  [types.ADD_KANBAN_ROUTING_RULE](_state, data) {
    _state.routingRules.push(data);
  },

  [types.DELETE_KANBAN_ROUTING_RULE](_state, ruleId) {
    _state.routingRules = _state.routingRules.filter(
      rule => rule.id !== ruleId
    );
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
